import { ProtobufEntity } from "@nova/constants"
import { Project } from "@nova/protobuf"
import { ProtoType } from "../serializer"

export type ElementType<T> = T extends (infer U)[] ? U : never
type Accessor<T> = T extends readonly unknown[]
    ? Partial<T[number]>
    : T extends object
      ? keyof T
      : never

type Next<T, A> = T extends readonly unknown[]
    ? T[number]
    : T extends object
      ? T[A & keyof T]
      : never

export type IsPathValid<T, P extends ReadonlyArray<string | object>> = P extends readonly []
    ? readonly []
    : P extends readonly [infer A, ...infer R]
      ? A extends Accessor<T>
          ? readonly [
                A,
                ...IsPathValid<Next<T, A>, R extends ReadonlyArray<string | object> ? R : never>,
            ]
          : never
      : never

type ValueAtPath<T, P extends readonly (string | object)[]> = P extends readonly []
    ? T
    : P extends readonly [infer A, ...infer R]
      ? ValueAtPath<Next<T, A & Accessor<T>>, R extends readonly (string | object)[] ? R : never>
      : never

export type ValueAtProjectPath<P extends readonly (string | object)[]> = ValueAtPath<Project, P>
type UnwrapValueAtPath<T, P extends readonly (string | object)[]> =
    ValueAtPath<T, P> extends ProtoType<ProtobufEntity>[]
        ? ElementType<ValueAtPath<T, P>>
        : ValueAtPath<T, P>
export type UnwrapValueAtProjectPath<P extends readonly (string | object)[]> =
    ValueAtProjectPath<P> extends ProtoType<ProtobufEntity>[]
        ? ElementType<ValueAtProjectPath<P>>
        : NonNullable<ValueAtProjectPath<P>>

export const get = <T extends Project, P extends readonly (string | object)[]>(
    project: T,
    path: P & IsPathValid<T, P>
): P extends readonly unknown[] ? ValueAtPath<T, P> : unknown => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime checks ensure proper typing
    let cur: any = project
    for (const item of path) {
        if (typeof item === "string") {
            cur = cur[item]
        } else {
            if (!Array.isArray(cur)) {
                throw new Error("Invalid path")
            }
            cur = cur.find((e) => Object.entries(item).every(([key, value]) => e[key] === value))
            if (!cur) throw new Error("Entity does not exist in path " + JSON.stringify(path))
        }
    }
    return cur
}

export const set = <T extends Project, P extends readonly (string | object)[]>(
    project: T,
    path: P,
    obj: UnwrapValueAtPath<T, P>
) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime checks ensure proper typing
    let cur: any = project
    for (const item of path.slice(0, -1)) {
        if (typeof item === "string") {
            cur = cur[item]
        } else {
            if (!Array.isArray(cur)) {
                throw new Error("Invalid path")
            }
            cur = cur.find((e) => Object.entries(item).every(([key, value]) => e[key] === value))
            if (!cur) throw new Error("Entity does not exist in path " + JSON.stringify(path))
        }
    }
    const lastKey = path.at(-1)!
    if (typeof lastKey === "string") {
        if (Array.isArray(cur[lastKey])) {
            cur[lastKey].push(obj)
        } else {
            if (cur[lastKey]) {
                Object.assign(cur[lastKey], obj)
            } else {
                cur[lastKey] = obj
            }
        }
    } else {
        if (Array.isArray(cur)) {
            const toUpdate = cur.find((e) =>
                Object.entries(lastKey).every(([key, value]) => e[key] === value)
            )
            Object.assign(toUpdate, obj)
        }
    }
}

export const del = <
    T extends Project,
    P extends readonly [...(string | object)[], string | object],
>(
    project: T,
    path: P
) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime checks ensure proper typing
    let cur: any = project
    for (const item of path.slice(0, -1)) {
        if (typeof item === "string") {
            cur = cur[item]
        } else {
            if (!Array.isArray(cur)) {
                throw new Error("Invalid path")
            }
            cur = cur.find((e) => Object.entries(item).every(([key, value]) => e[key] === value))
            if (!cur) throw new Error("Entity does not exist in path " + JSON.stringify(path))
        }
    }
    const lastObj = path.at(-1)!
    if (typeof lastObj === "object") {
        if (!Array.isArray(cur)) {
            throw new Error("Invalid path")
        }
        const index = cur.findIndex((e) =>
            Object.entries(lastObj).every(([key, value]) => e[key] === value)
        )
        if (index === -1)
            throw new Error("Could not find entitiy to delete " + JSON.stringify(lastObj))
        return cur.splice(index, 1)[0]
    } else {
        // string key: unset the property on the parent (e.g. flight.job = undefined)
        cur[lastObj] = undefined
    }
}
